import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import PageHeader from '../../components/PageHeader/PageHeader';
import StatCard from '../../components/StatCard/StatCard';
import EmptyState from '../../components/EmptyState/EmptyState';
import LineChart from '../Reports/LineChart';
import { getPerformance } from '../../services/AnalyticsService';
import logger from '../../utils/logger';
import { fmtMoney, fmtPct } from '../../utils/format';

const MODES = ['LIVE']; // LIVE-only platform (owner decision 2026-07-29)

const ENGINES = [
    { key: 'today', badge: 'gh-badge-info', labelId: 'analytics.engine.today' },
    { key: 'risk', badge: 'gh-badge-gold', labelId: 'analytics.engine.risk' },
    { key: 'futures', badge: 'gh-badge-danger', labelId: 'analytics.engine.futures' }
];

function Analytics() {
    const { formatMessage, locale } = useIntl();
    const t = (id) => formatMessage({ id });
    const history = useHistory();

    const [mode, setMode] = useState('LIVE');
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [notification, setNotification] = useState({});

    useEffect(() => {
        setLoaded(false);
        getPerformance(mode)
            .then(result => { setData(result || null); })
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
            .finally(() => setLoaded(true));
    }, [mode]);

    const overall = (data && data.overall) || {};
    const equityCurve = (data && Array.isArray(data.equityCurve)) ? data.equityCurve : [];
    const byEngine = (data && data.byEngine) || {};

    // True when the backend has nothing to show yet (no trades on any engine).
    const noData = loaded && (!data || (parseFloat(overall.trades) || 0) === 0);

    // Money node with green/red coloring and an optional leading sign.
    function moneyNode(v, { gold } = {}) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return <span className="num text-muted">—</span>;
        const cls = gold ? 'num' : `num ${n >= 0 ? 'gh-pos' : 'gh-neg'}`;
        return <span className={cls}>{n >= 0 ? '+' : ''}{fmtMoney(n)} <span className="fs-6 text-muted">USDC</span></span>;
    }

    // Adapt equityCurve -> LineChart's data shape (cumulative line).
    const chartData = (() => {
        if (!equityCurve.length) return null;
        const series = equityCurve.map(p => parseFloat(p.cumulative) || 0);
        const subs = equityCurve.map(p => {
            try { return new Date(p.date).toLocaleDateString(locale, { month: 'short', day: 'numeric' }); }
            catch (e) { return String(p.date || ''); }
        });
        const last = series.length ? series[series.length - 1] : 0;
        return {
            quote: 'USDC',
            series,
            subs,
            // LineChart prints (sellVolume - buyVolume) as its hero — feed it the
            // final cumulative P&L so the header matches the curve.
            sellVolume: last,
            buyVolume: 0,
            profitPerc: 0
        };
    })();

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <PageHeader title={t('analytics.title')} subtitle={t('analytics.subtitle')}>
                    <div className="btn-group" role="group" aria-label={t('analytics.modeFilter')}>
                        {MODES.map(m => (
                            <button
                                key={m}
                                type="button"
                                className={`btn btn-sm ${mode === m ? 'btn-gray-800' : 'btn-outline-secondary'}`}
                                onClick={() => setMode(m)}
                            >
                                {t('analytics.mode.' + m)}
                            </button>
                        ))}
                    </div>
                </PageHeader>

                {noData ? (
                    <div className="card card-body border-0 shadow mb-4">
                        <EmptyState
                            title={t('analytics.empty.title')}
                            text={t('analytics.empty.text')}
                            cta={{ label: t('analytics.empty.cta'), onClick: () => history.push('/today') }}
                        />
                    </div>
                ) : (
                    <React.Fragment>
                        {/* KPI row */}
                        <div className="gh-grid mb-4">
                            <div>
                                <StatCard
                                    label={t('analytics.realizedPnl')}
                                    gold
                                    value={moneyNode(overall.realizedPnl, { gold: true })}
                                    delta={Number.isFinite(parseFloat(overall.realizedPnl))
                                        ? (parseFloat(overall.realizedPnl) >= 0 ? t('analytics.inProfit') : t('analytics.inLoss'))
                                        : null}
                                    deltaType={parseFloat(overall.realizedPnl) >= 0 ? 'pos' : 'neg'}
                                />
                            </div>
                            <div>
                                <StatCard
                                    label={t('analytics.winRate')}
                                    value={<span className="num">{overall.winRate != null ? fmtPct(overall.winRate) : '—'}<span className="fs-6 text-muted">%</span></span>}
                                    sub={`${parseFloat(overall.wins) || 0} ${t('analytics.wins')} / ${parseFloat(overall.losses) || 0} ${t('analytics.losses')}`}
                                />
                            </div>
                            <div>
                                <StatCard
                                    label={t('analytics.trades')}
                                    value={<span className="num">{parseFloat(overall.trades) || 0}</span>}
                                    sub={t('analytics.tradesSub')}
                                />
                            </div>
                            <div>
                                <StatCard
                                    label={t('analytics.activeBots')}
                                    value={<span className="num">{parseFloat(overall.activeBots) || 0}</span>}
                                    sub={t('analytics.activeBotsSub')}
                                />
                            </div>
                            <div>
                                <StatCard
                                    label={t('analytics.avgWin')}
                                    value={<span className="num gh-pos">{overall.avgWin != null ? '+' + fmtMoney(overall.avgWin) : '—'} <span className="fs-6 text-muted">USDC</span></span>}
                                    sub={t('analytics.avgWinSub')}
                                />
                            </div>
                            <div>
                                <StatCard
                                    label={t('analytics.avgLoss')}
                                    value={<span className="num gh-neg">{overall.avgLoss != null ? fmtMoney(overall.avgLoss) : '—'} <span className="fs-6 text-muted">USDC</span></span>}
                                    sub={t('analytics.avgLossSub')}
                                />
                            </div>
                        </div>

                        {/* Equity curve */}
                        <div className="mb-2">
                            <h2 className="h5 mb-3">{t('analytics.equityCurve')}</h2>
                            {chartData
                                ? <LineChart data={chartData} />
                                : (
                                    <div className="card card-body border-0 shadow mb-4">
                                        <EmptyState title={t('analytics.curveEmpty')} text={t('analytics.curveEmptyHint')} />
                                    </div>
                                )}
                        </div>

                        {/* Per-engine breakdown */}
                        <h2 className="h5 mb-3">{t('analytics.byEngine')}</h2>
                        <div className="gh-grid gh-grid-wide mb-4">
                            {ENGINES.map(eng => {
                                const e = byEngine[eng.key] || {};
                                const pnl = parseFloat(e.realizedPnl);
                                const hasPnl = Number.isFinite(pnl);
                                return (
                                    <div key={eng.key}>
                                        <div className="card border-0 shadow h-100">
                                            <div className="card-header d-flex align-items-center justify-content-between">
                                                <span className={`gh-badge ${eng.badge}`}>{t(eng.labelId)}</span>
                                                <span className={`num fw-bold ${hasPnl ? (pnl >= 0 ? 'gh-pos' : 'gh-neg') : 'text-muted'}`}>
                                                    {hasPnl ? `${pnl >= 0 ? '+' : ''}${fmtMoney(pnl)}` : '—'}
                                                    <span className="fs-6 text-muted"> USDC</span>
                                                </span>
                                            </div>
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <small className="text-muted">{t('analytics.winRate')}</small>
                                                    <span className="num">{e.winRate != null ? fmtPct(e.winRate) + '%' : '—'}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <small className="text-muted">{t('analytics.trades')}</small>
                                                    <span className="num">{parseFloat(e.trades) || 0} ({parseFloat(e.wins) || 0}{t('analytics.winShort')})</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <small className="text-muted">{t('analytics.sessions')}</small>
                                                    <span className="num">{parseFloat(e.sessions) || 0}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </React.Fragment>
                )}

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Analytics;
